import { useState, useEffect } from "react";
import { Drawer, Stack, TextInput, PasswordInput, Group, Text, ActionIcon, Tooltip, Divider, Box } from "@mantine/core";
import { Button, Badge, Alert } from "@xplatform/design-system";
import {
    IconPlus,
    IconTrash,
    IconEdit,
    IconX,
    IconKey,
} from "@tabler/icons-react";
import type {
    CredentialViewModel,
    CreateCredentialRequest,
    UpdateCredentialRequest,
} from "@models/provider";

interface CredentialDrawerProps {
    opened: boolean;
    onClose: () => void;
    providerKey: string;
    credentials: CredentialViewModel[];
    onAdd: (data: CreateCredentialRequest) => Promise<void>;
    onUpdate: (id: string, data: UpdateCredentialRequest) => Promise<void>;
    onDelete: (id: string) => Promise<void>;
}

const emptyForm = {
    name: "",
    scope_key: "global",
    api_key: "",
    endpoint: "",
    api_version: "",
};

export const CredentialDrawer = ({
    opened,
    onClose,
    providerKey,
    credentials,
    onAdd,
    onUpdate,
    onDelete,
}: CredentialDrawerProps) => {
    const [form, setForm] = useState(emptyForm);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [showForm, setShowForm] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    useEffect(() => {
        if (!opened) {
            setShowForm(false);
            setEditingId(null);
            setForm(emptyForm);
            setError(null);
        }
    }, [opened]);

    const startEdit = (c: CredentialViewModel) => {
        setEditingId(c.id);
        setForm({
            name: c.name,
            scope_key: c.scopeKey,
            api_key: "",
            endpoint: c.endpoint ?? "",
            api_version: c.apiVersion ?? "",
        });
        setShowForm(true);
        setError(null);
    };

    const cancelForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm(emptyForm);
        setError(null);
    };

    const handleSubmit = async () => {
        if (!form.name.trim()) {
            setError("Name is required");
            return;
        }
        if (!editingId && !form.api_key.trim()) {
            setError("API key is required");
            return;
        }
        setError(null);
        setLoading(true);
        try {
            if (editingId) {
                const update: UpdateCredentialRequest = {
                    name: form.name,
                    scope_key: form.scope_key,
                };
                if (form.api_key) update.api_key = form.api_key;
                if (form.endpoint) update.endpoint = form.endpoint;
                if (form.api_version) update.api_version = form.api_version;
                await onUpdate(editingId, update);
            } else {
                await onAdd({
                    name: form.name,
                    scope_key: form.scope_key,
                    api_key: form.api_key,
                    endpoint: form.endpoint || undefined,
                    api_version: form.api_version || undefined,
                });
            }
            cancelForm();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Failed to save credential",
            );
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: string) => {
        setDeletingId(id);
        try {
            await onDelete(id);
        } catch (err) {
            console.error(err);
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <Drawer
            opened={opened}
            onClose={onClose}
            title={<Text fw={600}>Credentials - {providerKey}</Text>}
            position="right"
            size="md"
            portalProps={{ target: "#admin-portal-root" }}
        >
            <Stack gap="md">
                {credentials.length === 0 && !showForm && (
                    <Text c="dimmed" size="sm" ta="center" py="lg">
                        No credentials yet
                    </Text>
                )}

                {credentials.map((c) => (
                    <Box
                        key={c.id}
                        p="sm"
                        style={{
                            border: "1px solid var(--mantine-color-gray-2)",
                            borderRadius: 8,
                        }}
                    >
                        <Group justify="space-between" mb={4}>
                            <Group gap="xs">
                                <IconKey size={14} />
                                <Text size="sm" fw={500}>
                                    {c.name}
                                </Text>
                                <Badge size="xs" variant="light">
                                    {c.scopeKey}
                                </Badge>
                            </Group>
                            <Group gap={4}>
                                <Tooltip label="Edit">
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        onClick={() => startEdit(c)}
                                    >
                                        <IconEdit size={14} />
                                    </ActionIcon>
                                </Tooltip>
                                <Tooltip label="Delete">
                                    <ActionIcon
                                        size="sm"
                                        variant="subtle"
                                        color="red"
                                        loading={deletingId === c.id}
                                        onClick={() => handleDelete(c.id)}
                                    >
                                        <IconTrash size={14} />
                                    </ActionIcon>
                                </Tooltip>
                            </Group>
                        </Group>
                        {c.endpoint && (
                            <Text size="xs" c="dimmed">
                                {c.endpoint}
                                {c.apiVersion ? ` · v${c.apiVersion}` : ""}
                            </Text>
                        )}
                    </Box>
                ))}

                {!showForm && (
                    <Button
                        leftSection={<IconPlus size={14} />}
                        variant="light"
                        onClick={() => {
                            setShowForm(true);
                            setEditingId(null);
                            setForm(emptyForm);
                        }}
                    >
                        Add Credential
                    </Button>
                )}

                {showForm && (
                    <>
                        <Divider
                            label={editingId ? "Edit Credential" : "New Credential"}
                            labelPosition="left"
                        />
                        {error && (
                            <Alert color="red" icon={<IconX size={14} />}>
                                {error}
                            </Alert>
                        )}
                        <TextInput
                            label="Name"
                            placeholder="e.g. production-key"
                            required
                            value={form.name}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, name: e.currentTarget.value }))
                            }
                        />
                        <TextInput
                            label="Scope Key"
                            placeholder="global or module:extraction"
                            required
                            value={form.scope_key}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, scope_key: e.currentTarget.value }))
                            }
                        />
                        <PasswordInput
                            label={
                                editingId ? "API Key (leave blank to keep current)" : "API Key"
                            }
                            placeholder="sk-..."
                            required={!editingId}
                            value={form.api_key}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, api_key: e.currentTarget.value }))
                            }
                        />
                        <TextInput
                            label="Endpoint"
                            placeholder="https://api.openai.com"
                            value={form.endpoint}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, endpoint: e.currentTarget.value }))
                            }
                        />
                        <TextInput
                            label="API Version"
                            placeholder="2024-02-01"
                            value={form.api_version}
                            onChange={(e) =>
                                setForm((f) => ({ ...f, api_version: e.currentTarget.value }))
                            }
                        />
                        <Group justify="flex-end">
                            <Button variant="default" onClick={cancelForm} disabled={loading}>
                                Cancel
                            </Button>
                            <Button onClick={handleSubmit} loading={loading}>
                                {editingId ? "Update" : "Add"}
                            </Button>
                        </Group>
                    </>
                )}
            </Stack>
        </Drawer>
    );
};
